import type { Lens } from '@hookform/lenses';
import { match, P } from 'ts-pattern';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { IxmUserauthV1ProgramDetails } from '@interxfi/types/generated/api';
import { isDeepEqual } from '@interxfi/utilities/equality/deep';
import type { FormConfirmationGroup } from '../../../../components/form/confirmation/form-confirmation';
import { addDefinedValues } from '../../../../util/has-value';
import {
  defaultAddressFormValues,
  formValuesToAddress,
  getInitialFormValuesForAddress,
} from '../../../registration/shared-form/address/address-form';
import { getProgramDetailsListItems } from '../program-list-items';
import {
  defaultProgramCorrespondentInformationFormValues,
  ProgramCorrespondentInformationForm,
  programCorrespondentFormSchema,
} from './program-correspondent-information-form';
import {
  defaultProgramCustomerSupportContactFormValues,
  formValuesToProgramCustomerSupportContact,
  getInitialFormValuesForProgramCustomerSupportContact,
  onSubmitProgramCustomerSupportContactFormSchema,
  ProgramCustomerSupportContactForm,
  programCustomerSupportContactFormSchema,
} from './program-customer-support-contact-form';
import { ProgramInformationForm, programInformationFormSchema } from './program-information-form';
import {
  defaultProgramVendorsFormValues,
  formValuesToProgramVendors,
  getInitialFormValuesForProgramVendors,
  ProgramVendorsForm,
  programVendorsFormSchema,
} from './program-vendors-form';
import {
  defaultProgramRegistrationTypeFormValues,
  formValuesToProgramRegistrationType,
  getInitialFormValuesForProgramRegistrationType,
  ProgramRegistrationTypeDataForm,
  ProgramRegistrationTypeSelectionForm,
  programRegistrationTypeFormSchema,
} from './registration-type/program-registration-type-form';

export const programDetailsFormSchema = z.object({
  registrationType: programRegistrationTypeFormSchema,
  programInformation: programInformationFormSchema,
  correspondentInformation: programCorrespondentFormSchema,
  customerSupportContact: programCustomerSupportContactFormSchema,
  vendors: programVendorsFormSchema,
});

export const onSubmitProgramDetailsFormSchema = programDetailsFormSchema.safeExtend({
  customerSupportContact: onSubmitProgramCustomerSupportContactFormSchema,
});

export type ProgramDetailsFormValues = z.infer<typeof programDetailsFormSchema>;

export const defaultProgramDetailsFormValues: ProgramDetailsFormValues = {
  registrationType: defaultProgramRegistrationTypeFormValues,
  programInformation: { name: '' },
  correspondentInformation: defaultProgramCorrespondentInformationFormValues,
  customerSupportContact: defaultProgramCustomerSupportContactFormValues,
  vendors: defaultProgramVendorsFormValues,
};

export function getInitialFormValuesForProgramDetails(data: IxmUserauthV1ProgramDetails | undefined): ProgramDetailsFormValues {
  if (!data) {
    return defaultProgramDetailsFormValues;
  }

  return {
    registrationType: getInitialFormValuesForProgramRegistrationType(data.registrationType),
    programInformation: {
      name: data?.name || '',
    },
    correspondentInformation: {
      legalName: data?.legalName || '',
      dba: data?.dba || '',
      webDomains: (data?.webDomains || ['']).map((domain) => ({ url: domain })),
      taxIdentifier: data?.taxIdentifier || '',
      mainOfficeAddress: getInitialFormValuesForAddress(data?.mainOfficeAddress) || defaultAddressFormValues,
      mailingAddress: { isProviding: Boolean(data?.mailingAddress), value: getInitialFormValuesForAddress(data.mailingAddress) },
      mailingAddressIsSameAsMainOffice: data?.mainOfficeAddress ? isDeepEqual(data?.mailingAddress, data?.mainOfficeAddress) : false,
    },
    customerSupportContact: getInitialFormValuesForProgramCustomerSupportContact(data?.customerSupportContacts, data?.mainOfficeAddress),
    vendors: getInitialFormValuesForProgramVendors(data?.vendors),
  };
}

export function formValuesToProgramDetails(values: ProgramDetailsFormValues): Omit<IxmUserauthV1ProgramDetails, 'contacts'> {
  const mappedMainOfficeAddress = formValuesToAddress(values.correspondentInformation.mainOfficeAddress);

  return addDefinedValues<Omit<IxmUserauthV1ProgramDetails, 'contacts'>>(
    {
      registrationType: formValuesToProgramRegistrationType(values.registrationType),
      name: values.programInformation.name,
      legalName: values.correspondentInformation.legalName,
      taxIdentifier: values.correspondentInformation.taxIdentifier,
      mainOfficeAddress: mappedMainOfficeAddress,
      vendors: formValuesToProgramVendors(values.vendors),
      customerSupportContacts: formValuesToProgramCustomerSupportContact(
        values.customerSupportContact,
        values.programInformation.name,
        mappedMainOfficeAddress,
      ),
    },
    {
      dba: values.correspondentInformation.dba || undefined,
      webDomains: values.correspondentInformation.webDomains.length ? values.correspondentInformation.webDomains.map((d) => d.url) : undefined,
      mailingAddress: match(values.correspondentInformation)
        .with({ mailingAddress: { isProviding: true }, mailingAddressIsSameAsMainOffice: true }, () => mappedMainOfficeAddress)
        .with({ mailingAddress: { isProviding: true, value: P.not(P.nullish) } }, ({ mailingAddress }) => formValuesToAddress(mailingAddress.value))
        .otherwise(() => undefined),
    },
  );
}

export function mapProgramDetailsToFormConfirmationGroups(values: IxmUserauthV1ProgramDetails | undefined): FormConfirmationGroup[] {
  const items = getProgramDetailsListItems(values);

  return [
    {
      id: 'programInformation',
      title: 'Program Information',
      list: {
        items: [items.name, { ...items.registrationType.typeName, title: 'Registration Type' }],
      },
    },
    {
      id: 'correspondentInformation',
      title: 'Correspondent Information',
      list: {
        items: [
          items.legalName,
          items.webDomains,
          items.mainOfficeAddress.formattedMultiLine,
          items.mailingAddress.formattedMultiLine,
          items.dba,
          items.taxIdentifier,
          ...match(items.registrationType)
            .with({ brokerDealer: P.not(P.nullish) }, (t) => [t.brokerDealer.value.crdNumber, t.brokerDealer.value.secRegistrationNumber])
            .with({ investmentAdviser: P.not(P.nullish) }, (t) => [
              t.investmentAdviser.value.crdNumber,
              t.investmentAdviser.value.secRegistrationNumber,
            ])
            .otherwise(() => []),
        ],
      },
    },
    {
      id: 'customerSupportContact',
      title: 'Customer Support Contact',
      list: {
        items: [
          items.customerSupportContacts.displayName,
          items.customerSupportContacts.website,
          items.customerSupportContacts.emailAddress,
          items.customerSupportContacts.phone,
          items.customerSupportContacts.phoneTaxDocuments,
          items.customerSupportContacts.mailingAddress.formattedMultiLine,
        ],
      },
    },
    {
      id: 'vendors',
      title: 'Correspondent Vendors',
      list: {
        items: [items.vendors.cipIdentity, items.vendors.cipOfac, items.vendors.bankAccountValidation],
      },
    },
  ];
}

interface ProgramDetailsFormProps {
  lens: Lens<ProgramDetailsFormValues>;
  showTooltips?: boolean;
}

export function ProgramDetailsForm({ lens, showTooltips }: ProgramDetailsFormProps) {
  return (
    <VerticalStack gap={7}>
      <ProgramInformationForm
        lens={lens.focus('programInformation')}
        registrationTypeField={<ProgramRegistrationTypeSelectionForm lens={lens.focus('registrationType')} />}
        showTooltips={showTooltips}
      />

      <ProgramCorrespondentInformationForm
        lens={lens.focus('correspondentInformation')}
        registrationTypeDataFields={<ProgramRegistrationTypeDataForm lens={lens.focus('registrationType')} />}
      />

      <ProgramCustomerSupportContactForm lens={lens.focus('customerSupportContact')} showTooltips={showTooltips} />

      <ProgramVendorsForm lens={lens.focus('vendors')} />
    </VerticalStack>
  );
}
